<script setup>
import { ref, computed, watch } from 'vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Textarea from 'dashboard/components-next/textarea/TextArea.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Drawer from '../../website/components/Drawer.vue';
import ApiConnectorConfig from './ApiConnectorConfig.vue';
import DriveFolderSelector from './DriveFolderSelector.vue';
import PropertySection from './properties/PropertySection.vue';
import JsonListEditor from './properties/JsonListEditor.vue';
import IconPicker from './properties/IconPicker.vue';
import WidgetStyleSettings from './properties/WidgetStyleSettings.vue';
import axios from 'axios';
import Auth from 'dashboard/api/auth';
import { usePortalAccount } from '../composables/usePortalAccount';

const props = defineProps({
  widget: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['update', 'close', 'delete']);

const activeTab = ref('appearance');
const localWidget = ref(JSON.parse(JSON.stringify(props.widget)));

const { accountId } = usePortalAccount();

const getAuthHeaders = () => {
  const headers = {
    'Content-Type': 'application/json',
  };

  if (Auth.hasAuthCookie()) {
    const authData = Auth.getAuthData();
    if (authData && authData['access-token']) {
      headers['access-token'] = authData['access-token'];
      headers['token-type'] = authData['token-type'];
      headers.client = authData.client;
      headers.expiry = authData.expiry;
      headers.uid = authData.uid;
    }
  }

  return headers;
};

const availableInboxes = ref([]);
const fetchInboxes = async () => {
  try {
    const response = await axios.get(
      `/api/v1/accounts/${accountId.value}/inboxes`,
      {
        headers: getAuthHeaders(),
      }
    );
    availableInboxes.value = response.data?.payload || response.data || [];
  } catch (e) {
    // Erreur silencieuse - l'utilisateur verra un état vide
  }
};

const updateProp = (key, value) => {
  let finalValue = value;

  // Validation basique pour les champs JSON
  if (
    ['series', 'categories', 'pins'].includes(key) &&
    typeof value === 'string'
  ) {
    try {
      finalValue = JSON.parse(value);
    } catch (e) {
      // Garder la string si JSON invalide (en cours de frappe)
      return;
    }
  }

  if (!localWidget.value.props) {
    localWidget.value.props = {};
  }

  localWidget.value.props = {
    ...localWidget.value.props,
    [key]: finalValue,
  };

  // Émettre l'update immédiatement pour le live preview
  emit('update', JSON.parse(JSON.stringify(localWidget.value)));
};

const toggleInbox = inboxId => {
  const currentIds = localWidget.value.props?.inboxIds || [];
  const index = currentIds.indexOf(inboxId);
  if (index === -1) {
    updateProp('inboxIds', [...currentIds, inboxId]);
  } else {
    updateProp(
      'inboxIds',
      currentIds.filter(id => id !== inboxId)
    );
  }
};

watch(
  () => props.widget,
  newWidget => {
    localWidget.value = JSON.parse(JSON.stringify(newWidget));
    if (newWidget.type === 'chat') {
      fetchInboxes();
    }
  },
  { immediate: true, deep: true }
);

const updateWidget = newWidget => {
  localWidget.value = newWidget;
  emit('update', JSON.parse(JSON.stringify(localWidget.value)));
};

const handleSave = () => {
  emit('update', JSON.parse(JSON.stringify(localWidget.value)));
  emit('close');
};

const handleDelete = () => {
  // eslint-disable-next-line no-alert
  if (window.confirm('Supprimer ce widget ?')) {
    emit('delete');
  }
};

const hasApiConnector = computed(() => {
  return !!localWidget.value.props?.apiConnector;
});

const openSections = ref({
  general: true,
  content: true,
  items: true,
  legend: false,
  appearance: false,
});

const toggleSection = section => {
  openSections.value[section] = !openSections.value[section];
};

// Field definitions for JsonListEditor
const quickActionFields = [
  {
    key: 'label',
    label: 'Label',
    type: 'text',
    placeholder: 'Nouvelle conversation',
  },
  { key: 'icon', label: 'Icône', type: 'icon', default: 'i-lucide-plus' },
  {
    key: 'action',
    label: 'Action ID',
    type: 'text',
    placeholder: 'new_conversation',
  },
];

const statsCardFields = [
  {
    key: 'label',
    label: 'Libellé',
    type: 'text',
    placeholder: 'Conversations',
  },
  { key: 'value', label: 'Valeur', type: 'text', placeholder: '124' },
  {
    key: 'icon',
    label: 'Icône',
    type: 'icon',
    default: 'i-lucide-message-square',
  },
  {
    key: 'color',
    label: 'Couleur',
    type: 'select',
    options: ['primary', 'blue', 'emerald', 'ruby', 'amber', 'purple'],
    default: 'primary',
  },
  {
    key: 'trend',
    label: 'Tendance',
    type: 'select',
    options: ['none', 'up', 'down', 'neutral'],
    default: 'none',
  },
  {
    key: 'trendValue',
    label: 'Valeur tendance',
    type: 'text',
    placeholder: '+12%',
  },
];

const timelineFields = [
  {
    key: 'label',
    label: 'Étape',
    type: 'text',
    placeholder: 'Visite technique',
  },
  {
    key: 'status',
    label: 'Statut',
    type: 'select',
    options: ['pending', 'current', 'completed'],
    default: 'pending',
  },
  {
    key: 'assignee',
    label: 'Responsable',
    type: 'text',
    placeholder: 'Jean Dupont',
  },
];

const infoPropertyFields = [
  { key: 'label', label: 'Label', type: 'text', placeholder: 'Adresse' },
  {
    key: 'value',
    label: 'Valeur',
    type: 'text',
    placeholder: '123 Rue de Paris',
  },
  { key: 'icon', label: 'Icône', type: 'icon', default: 'i-lucide-map-pin' },
];

const recentActivityFields = [
  {
    key: 'title',
    label: 'Titre',
    type: 'text',
    placeholder: 'Nouveau message',
  },
  {
    key: 'description',
    label: 'Description',
    type: 'text',
    placeholder: "Détails de l'activité",
  },
  { key: 'time', label: 'Temps', type: 'text', placeholder: 'Il y a 5 min' },
  { key: 'icon', label: 'Icône', type: 'icon', default: 'i-lucide-activity' },
  {
    key: 'type',
    label: 'Type',
    type: 'select',
    options: ['message', 'update', 'file'],
    default: 'message',
  },
];

const mapLegendFields = [
  {
    key: 'id',
    label: 'ID du style',
    type: 'text',
    placeholder: 'primary',
    default: '',
  },
  {
    key: 'label',
    label: 'Libellé',
    type: 'text',
    placeholder: 'Principal',
    default: '',
  },
  {
    key: 'color',
    label: 'Couleur',
    type: 'color',
    placeholder: '#3860F9',
    default: '#3860F9',
  },
];

// Styles par défaut pour la carte
const defaultMapStyles = [
  { id: 'primary', label: 'Principal', color: '#3860F9' },
  { id: 'success', label: 'Finalisé', color: '#16A34A' },
  { id: 'warning', label: 'En travaux', color: '#FF8C42' },
  { id: 'danger', label: 'Finalisé avec réserves', color: '#DC2626' },
  { id: 'info', label: 'Étude / Devis', color: '#3B82F6' },
];

// Styles personnalisés ou par défaut
const mapStyles = computed(() => {
  return localWidget.value.props?.legendStyles || defaultMapStyles;
});

// Options dynamiques pour le select de style dans les pins
const mapPinStyleOptions = computed(() => {
  return mapStyles.value.map(style => ({
    value: style.id,
    label: style.label,
  }));
});

const mapPinFields = computed(() => [
  {
    key: 'title',
    label: 'Titre',
    type: 'text',
    placeholder: 'Paris',
    default: '',
  },
  {
    key: 'description',
    label: 'Description',
    type: 'text',
    placeholder: 'Capitale de la France',
    default: '',
  },
  {
    key: 'address',
    label: 'Adresse postale',
    type: 'address',
    placeholder: '123 Rue de Paris, 75001 Paris, France',
    default: '',
  },
  {
    key: 'lat',
    label: 'Latitude',
    type: 'number',
    placeholder: '48.8566',
    step: '0.000001',
    default: '',
    hidden: false,
  },
  {
    key: 'lng',
    label: 'Longitude',
    type: 'number',
    placeholder: '2.3522',
    step: '0.000001',
    default: '',
    hidden: false,
  },
  {
    key: 'style',
    label: 'Style',
    type: 'select',
    options: mapPinStyleOptions.value,
    default: mapPinStyleOptions.value[0]?.value || 'primary',
  },
]);
</script>

<template>
  <Drawer :show="true" size="md" @close="emit('close')">
    <div class="flex flex-col h-full bg-n-background font-inter">
      <!-- Header Moderne -->
      <div
        class="p-6 border-b border-n-weak bg-n-solid-1 sticky top-0 z-20 shadow-sm"
      >
        <div class="flex items-center justify-between mb-1">
          <div class="flex items-center gap-3">
            <div
              class="p-2.5 rounded-xl bg-n-brand/10 border border-n-brand/20 shadow-sm"
            >
              <span
                class="size-5 text-n-brand"
                :class="[widget.icon || 'i-lucide-settings-2']"
              />
            </div>
            <div>
              <h2 class="text-lg font-bold text-n-slate-12 tracking-tight">
                Paramètres du widget
              </h2>
              <p
                class="text-[11px] font-bold text-n-slate-10 uppercase tracking-widest"
              >
                {{ widget.label }}
              </p>
            </div>
          </div>
          <button
            class="p-2 rounded-lg hover:bg-n-alpha-2 text-n-slate-11 hover:text-n-slate-12 transition-colors"
            @click="emit('close')"
          >
            <span class="i-lucide-x size-5" />
          </button>
        </div>
      </div>

      <!-- Tabs Modernes -->
      <div
        class="flex gap-1 px-6 bg-n-solid-1 border-b border-n-weak sticky top-[89px] z-20 shadow-sm"
      >
        <button
          v-for="tab in [
            {
              id: 'appearance',
              label: 'Contenu',
              icon: 'i-lucide-layout-template',
            },
            { id: 'data', label: 'Données', icon: 'i-lucide-database' },
            { id: 'style', label: 'Design', icon: 'i-lucide-palette' },
          ]"
          :key="tab.id"
          class="flex items-center gap-2 px-4 py-3 text-sm font-bold transition-all relative group"
          :class="
            activeTab === tab.id
              ? 'text-n-brand'
              : 'text-n-slate-10 hover:text-n-slate-12'
          "
          @click="activeTab = tab.id"
        >
          <span class="size-4" :class="[tab.icon]" />
          {{ tab.label }}
          <div
            v-if="activeTab === tab.id"
            class="absolute bottom-0 left-0 w-full h-0.5 bg-n-brand rounded-t-full"
          />
          <div
            v-if="tab.id === 'data' && hasApiConnector"
            class="absolute top-2 right-2 size-2 bg-n-teal-9 rounded-full ring-2 ring-n-solid-1 animate-pulse"
          />
        </button>
      </div>

      <!-- Content -->
      <div class="flex-1 overflow-y-auto">
        <!-- Contenu / Apparence Tab -->
        <div
          v-if="activeTab === 'appearance'"
          class="divide-y divide-n-weak text-n-slate-12"
        >
          <!-- Configuration Générale -->
          <PropertySection
            title="Général"
            icon="i-lucide-settings"
            :open="openSections.general"
            @toggle="toggleSection('general')"
          >
            <div class="space-y-4 pt-2">
              <div class="space-y-1.5">
                <label
                  class="text-xs font-semibold text-n-slate-11 uppercase tracking-wider"
                  >Titre du widget</label>
                <Input
                  :model-value="localWidget.props?.title || ''"
                  placeholder="Ex: Statistiques de vente"
                  class="shadow-sm"
                  @update:model-value="updateProp('title', $event)"
                />
              </div>
            </div>
          </PropertySection>

          <!-- Paramètres spécifiques par type -->
          <PropertySection
            title="Contenu"
            icon="i-lucide-edit-3"
            :open="openSections.content"
            @toggle="toggleSection('content')"
          >
            <div class="space-y-5 pt-2">
              <!-- Text Widget -->
              <template v-if="widget.type === 'text'">
                <div class="space-y-1.5">
                  <label
                    class="text-xs font-semibold text-n-slate-11 uppercase tracking-wider"
                    >Contenu</label>
                  <Textarea
                    :model-value="localWidget.props?.content || ''"
                    rows="8"
                    placeholder="Entrez votre texte ici..."
                    class="shadow-sm font-medium"
                    @update:model-value="updateProp('content', $event)"
                  />
                </div>
                <div class="grid grid-cols-2 gap-4">
                  <div class="space-y-1.5">
                    <label
                      class="text-xs font-semibold text-n-slate-11 uppercase tracking-wider"
                      >Taille</label>
                    <select
                      :value="localWidget.props?.fontSize || 'base'"
                      class="w-full h-9 px-3 text-sm rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-blue-9 transition-all shadow-sm"
                      @change="updateProp('fontSize', $event.target.value)"
                    >
                      <option value="sm">Petit</option>
                      <option value="base">Normal</option>
                      <option value="lg">Grand</option>
                      <option value="xl">Très grand</option>
                    </select>
                  </div>
                  <div class="space-y-1.5">
                    <label
                      class="text-xs font-semibold text-n-slate-11 uppercase tracking-wider"
                      >Alignement</label>
                    <select
                      :value="localWidget.props?.alignment || 'left'"
                      class="w-full h-9 px-3 text-sm rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-blue-9 transition-all shadow-sm"
                      @change="updateProp('alignment', $event.target.value)"
                    >
                      <option value="left">Gauche</option>
                      <option value="center">Centre</option>
                      <option value="right">Droite</option>
                    </select>
                  </div>
                </div>
              </template>

              <!-- Metric Widget -->
              <template v-if="widget.type === 'metric'">
                <div
                  v-if="hasApiConnector"
                  class="p-4 bg-n-blue-1/50 border border-n-blue-6/30 rounded-2xl flex items-start gap-3"
                >
                  <div class="p-2 rounded-xl bg-n-blue-2 text-n-blue-11">
                    <span class="i-lucide-info size-4" />
                  </div>
                  <p class="text-xs font-medium text-n-blue-11 leading-relaxed">
                    Les valeurs sont récupérées dynamiquement depuis l'API
                    connectée.
                  </p>
                </div>
                <div class="space-y-4">
                  <div class="space-y-1.5">
                    <label
                      class="text-xs font-semibold text-n-slate-11 uppercase tracking-wider"
                      >Valeur (statique)</label>
                    <Input
                      :model-value="localWidget.props?.value || ''"
                      placeholder="1,234"
                      :disabled="hasApiConnector"
                      class="shadow-sm font-bold text-lg"
                      @update:model-value="updateProp('value', $event)"
                    />
                  </div>
                  <div class="space-y-1.5">
                    <label
                      class="text-xs font-semibold text-n-slate-11 uppercase tracking-wider"
                      >Sous-valeur / Tendance</label>
                    <Input
                      :model-value="localWidget.props?.subvalue || ''"
                      placeholder="+12% vs mois dernier"
                      :disabled="hasApiConnector"
                      class="shadow-sm"
                      @update:model-value="updateProp('subvalue', $event)"
                    />
                  </div>
                  <div class="grid grid-cols-2 gap-4">
                    <div class="space-y-1.5">
                      <label
                        class="text-xs font-semibold text-n-slate-11 uppercase tracking-wider"
                        >Tendance</label>
                      <select
                        :value="localWidget.props?.trend || 'neutral'"
                        class="w-full h-9 px-3 text-sm rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-blue-9 transition-all shadow-sm"
                        :disabled="hasApiConnector"
                        @change="updateProp('trend', $event.target.value)"
                      >
                        <option value="neutral">Neutre</option>
                        <option value="up">Hausse (Vert)</option>
                        <option value="down">Baisse (Rouge)</option>
                      </select>
                    </div>
                    <div class="space-y-1.5">
                      <label
                        class="text-xs font-semibold text-n-slate-11 uppercase tracking-wider"
                        >Format</label>
                      <select
                        :value="localWidget.props?.format || 'number'"
                        class="w-full h-9 px-3 text-sm rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-blue-9 transition-all shadow-sm"
                        @change="updateProp('format', $event.target.value)"
                      >
                        <option value="number">Nombre</option>
                        <option value="currency">Monnaie (€)</option>
                        <option value="percentage">Pourcentage (%)</option>
                      </select>
                    </div>
                  </div>
                  <div class="space-y-1.5">
                    <label
                      class="text-xs font-semibold text-n-slate-11 uppercase tracking-wider"
                      >Icône</label>
                    <IconPicker
                      :model-value="
                        localWidget.props?.icon || 'i-lucide-activity'
                      "
                      @update:model-value="updateProp('icon', $event)"
                    />
                  </div>
                </div>
              </template>

              <!-- Chart Widget -->
              <template v-if="widget.type === 'chart'">
                <div class="space-y-4">
                  <div class="space-y-1.5">
                    <label
                      class="text-xs font-semibold text-n-slate-11 uppercase tracking-wider"
                      >Type de graphique</label
                    >
                    <select
                      :value="localWidget.props?.chartType || 'bar'"
                      class="w-full h-9 px-3 text-sm rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-blue-9 transition-all shadow-sm"
                      @change="updateProp('chartType', $event.target.value)"
                    >
                      <option value="bar">Barres Groupées</option>
                      <option value="stacked">Barres Empilées</option>
                      <option value="line">Ligne</option>
                      <option value="area">Aire</option>
                      <option value="pie">Camembert</option>
                      <option value="donut">Donut</option>
                    </select>
                  </div>
                  <div class="space-y-1.5">
                    <label
                      class="text-xs font-semibold text-n-slate-11 uppercase tracking-wider"
                      >Séries de données (JSON)</label
                    >
                    <Textarea
                      :model-value="
                        typeof localWidget.props?.series === 'string'
                          ? localWidget.props.series
                          : JSON.stringify(
                              localWidget.props?.series || [],
                              null,
                              2
                            )
                      "
                      rows="6"
                      class="font-mono text-[11px] shadow-sm"
                      :disabled="hasApiConnector"
                      @update:model-value="updateProp('series', $event)"
                    />
                  </div>
                  <div class="space-y-1.5">
                    <label
                      class="text-xs font-semibold text-n-slate-11 uppercase tracking-wider"
                      >Catégories (JSON)</label
                    >
                    <Textarea
                      :model-value="
                        typeof localWidget.props?.categories === 'string'
                          ? localWidget.props.categories
                          : JSON.stringify(
                              localWidget.props?.categories || [],
                              null,
                              2
                            )
                      "
                      rows="3"
                      class="font-mono text-[11px] shadow-sm"
                      :disabled="hasApiConnector"
                      @update:model-value="updateProp('categories', $event)"
                    />
                  </div>
                </div>
              </template>

              <!-- Table Widget -->
              <template v-if="widget.type === 'table'">
                <div class="space-y-4">
                  <div class="space-y-1.5">
                    <label
                      class="text-xs font-semibold text-n-slate-11 uppercase tracking-wider"
                      >En-têtes (virgules)</label
                    >
                    <Input
                      :model-value="
                        Array.isArray(localWidget.props?.headers)
                          ? localWidget.props.headers.join(', ')
                          : localWidget.props?.headers || ''
                      "
                      placeholder="Nom, Email, Statut"
                      @update:model-value="
                        updateProp(
                          'headers',
                          $event.split(',').map(h => h.trim())
                        )
                      "
                    />
                  </div>
                  <div class="space-y-1.5">
                    <label
                      class="text-xs font-semibold text-n-slate-11 uppercase tracking-wider"
                      >Lignes (CSV)</label
                    >
                    <Textarea
                      :model-value="
                        Array.isArray(localWidget.props?.rows)
                          ? localWidget.props.rows
                              .map(r => (Array.isArray(r) ? r.join(', ') : r))
                              .join('\n')
                          : ''
                      "
                      rows="6"
                      placeholder="Jean, jean@mail.com, Actif&#10;Marie, marie@mail.com, Inactif"
                      class="shadow-sm font-mono text-xs"
                      @update:model-value="
                        updateProp(
                          'rows',
                          $event
                            .split('\n')
                            .filter(line => line.trim())
                            .map(row => row.split(',').map(c => c.trim()))
                        )
                      "
                    />
                    <p class="text-[10px] font-medium text-n-slate-9 italic">
                      Séparez les colonnes par des virgules, une ligne par
                      rangée.
                    </p>
                  </div>
                </div>
              </template>

              <!-- Conversations/Contacts/Orders Lists -->
              <template
                v-if="
                  [
                    'conversations_list',
                    'contacts_list',
                    'orders_list',
                  ].includes(widget.type)
                "
              >
                <div class="space-y-4">
                  <div class="space-y-1.5">
                    <label
                      class="text-xs font-semibold text-n-slate-11 uppercase tracking-wider"
                      >Limite d'affichage</label
                    >
                    <Input
                      :model-value="localWidget.props?.limit || 5"
                      type="number"
                      placeholder="5"
                      class="shadow-sm"
                      @update:model-value="
                        updateProp('limit', parseInt($event) || 5)
                      "
                    />
                  </div>
                  <div
                    v-if="widget.type === 'conversations_list'"
                    class="space-y-1.5"
                  >
                    <label
                      class="text-xs font-semibold text-n-slate-11 uppercase tracking-wider"
                      >Statut par défaut</label
                    >
                    <select
                      :value="localWidget.props?.status || 'open'"
                      class="w-full h-9 px-3 text-sm rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-blue-9 transition-all shadow-sm"
                      @change="updateProp('status', $event.target.value)"
                    >
                      <option value="open">Ouvert</option>
                      <option value="resolved">Résolu</option>
                      <option value="pending">En attente</option>
                      <option value="snoozed">Reporté</option>
                    </select>
                  </div>
                </div>
              </template>

              <!-- Drive Widget -->
              <template v-if="widget.type === 'drive'">
                <DriveFolderSelector
                  :selected-folder="localWidget.props?.rootFolder || '/'"
                  @update="folder => updateProp('rootFolder', folder)"
                />
              </template>

              <!-- Map Widget -->
              <template v-if="widget.type === 'map'">
                <div class="space-y-4">
                  <div class="space-y-1.5">
                    <label
                      class="text-xs font-semibold text-n-slate-11 uppercase tracking-wider"
                      >Région par défaut</label
                    >
                    <select
                      :value="localWidget.props?.region || 'france'"
                      class="w-full h-9 px-3 text-sm rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-blue-9 transition-all shadow-sm"
                      @change="updateProp('region', $event.target.value)"
                    >
                      <option value="france">France</option>
                      <option value="paris">Paris</option>
                      <option value="lyon">Lyon</option>
                      <option value="marseille">Marseille</option>
                      <option value="toulouse">Toulouse</option>
                      <option value="nice">Nice</option>
                    </select>
                  </div>
                </div>
              </template>

              <!-- Chat Widget -->
              <template v-if="widget.type === 'chat'">
                <div class="space-y-4">
                  <div class="space-y-3">
                    <label
                      class="text-xs font-semibold text-n-slate-11 uppercase tracking-wider"
                      >Inboxes autorisées</label>
                    <div
                      class="grid grid-cols-1 gap-2 max-h-60 overflow-y-auto pr-2 custom-scrollbar text-n-slate-12"
                    >
                      <div
                        v-for="inbox in availableInboxes"
                        :key="inbox.id"
                        class="flex items-center gap-3 p-3 rounded-xl border border-n-weak hover:bg-n-alpha-1 cursor-pointer transition-all"
                        :class="
                          localWidget.props?.inboxIds?.includes(inbox.id)
                            ? 'border-n-brand bg-n-brand/5 ring-1 ring-n-brand'
                            : ''
                        "
                        @click="toggleInbox(inbox.id)"
                      >
                        <div
                          class="size-4 rounded border border-n-slate-6 flex items-center justify-center transition-colors"
                          :class="
                            localWidget.props?.inboxIds?.includes(inbox.id)
                              ? 'bg-n-brand border-n-brand'
                              : 'bg-white text-n-slate-12'
                          "
                        >
                          <span
                            v-if="
                              localWidget.props?.inboxIds?.includes(inbox.id)
                            "
                            class="i-lucide-check size-3 text-white"
                          />
                        </div>
                        <div class="flex-1 min-w-0">
                          <p class="text-sm font-bold truncate">
                            {{ inbox.name }}
                          </p>
                          <p
                            class="text-[10px] font-medium text-n-slate-10 uppercase tracking-widest"
                          >
                            {{ inbox.channel_type.replace('Channel::', '') }}
                          </p>
                        </div>
                      </div>
                    </div>
                    <p
                      v-if="availableInboxes.length === 0"
                      class="text-xs text-n-slate-9 italic text-n-slate-12"
                    >
                      Chargement des inboxes...
                    </p>
                  </div>
                </div>
              </template>
            </div>
          </PropertySection>

          <!-- Listes d'éléments (JsonListEditor) -->
          <PropertySection
            v-if="
              [
                'quick_actions',
                'stats_cards',
                'timeline',
                'info_properties',
                'map',
                'recent_activities',
              ].includes(widget.type)
            "
            title="Éléments de la liste"
            icon="i-lucide-list-checks"
            :open="openSections.items"
            @toggle="toggleSection('items')"
          >
            <div class="pt-2">
              <JsonListEditor
                v-if="widget.type === 'quick_actions'"
                :model-value="localWidget.props?.actions"
                :fields="quickActionFields"
                add-button-label="Ajouter une action"
                @update:model-value="updateProp('actions', $event)"
              />
              <JsonListEditor
                v-if="widget.type === 'stats_cards'"
                :model-value="localWidget.props?.cards"
                :fields="statsCardFields"
                add-button-label="Ajouter une carte"
                @update:model-value="updateProp('cards', $event)"
              />
              <JsonListEditor
                v-if="widget.type === 'timeline'"
                :model-value="localWidget.props?.steps"
                :fields="timelineFields"
                add-button-label="Ajouter une étape"
                @update:model-value="updateProp('steps', $event)"
              />
              <JsonListEditor
                v-if="widget.type === 'info_properties'"
                :model-value="localWidget.props?.items"
                :fields="infoPropertyFields"
                add-button-label="Ajouter une propriété"
                @update:model-value="updateProp('items', $event)"
              />
              <JsonListEditor
                v-if="widget.type === 'recent_activities'"
                :model-value="localWidget.props?.activities"
                :fields="recentActivityFields"
                title-field="title"
                add-button-label="Ajouter une activité"
                @update:model-value="updateProp('activities', $event)"
              />
              <JsonListEditor
                v-if="widget.type === 'map'"
                :model-value="localWidget.props?.pins"
                :fields="mapPinFields"
                title-field="title"
                add-button-label="Ajouter un marqueur"
                @update:model-value="updateProp('pins', $event)"
              />
            </div>
          </PropertySection>

          <!-- Styles/Légendes pour la carte -->
          <PropertySection
            v-if="widget.type === 'map'"
            title="Styles et légendes"
            icon="i-lucide-palette"
            :open="openSections.legend"
            @toggle="toggleSection('legend')"
          >
            <div class="pt-2">
              <JsonListEditor
                :model-value="
                  localWidget.props?.legendStyles || defaultMapStyles
                "
                :fields="mapLegendFields"
                title-field="label"
                add-button-label="Ajouter un style"
                @update:model-value="updateProp('legendStyles', $event)"
              />
            </div>
          </PropertySection>
        </div>

        <!-- Data Tab -->
        <div v-if="activeTab === 'data'" class="p-6">
          <ApiConnectorConfig
            :widget="localWidget"
            :account-id="accountId"
            @update="updateWidget"
            @close="activeTab = 'appearance'"
          />
        </div>

        <!-- Style Tab -->
        <div v-if="activeTab === 'style'" class="p-6">
          <WidgetStyleSettings :widget="localWidget" @update="updateWidget" />

          <PropertySection
            title="Icône du widget"
            icon="i-lucide-image"
            class="mt-6 border-t border-n-weak"
          >
            <div class="pt-2 text-n-slate-12">
              <IconPicker
                :model-value="localWidget.icon"
                @update:model-value="
                  emit('update', { ...localWidget, icon: $event })
                "
              />
            </div>
          </PropertySection>
        </div>
      </div>

      <!-- Footer Premium -->
      <div
        class="p-6 border-t border-n-weak bg-n-solid-1 shadow-inner flex justify-between gap-3 sticky bottom-0 z-20"
      >
        <Button
          label="Supprimer"
          icon="i-lucide-trash-2"
          color="ruby"
          variant="ghost"
          class="hover:bg-n-ruby-2"
          @click="handleDelete"
        />
        <div class="flex gap-2">
          <Button
            label="Sauvegarder"
            variant="solid"
            color="blue"
            icon="i-lucide-check"
            @click="handleSave"
          />
        </div>
      </div>
    </div>
  </Drawer>
</template>

<style scoped>
.custom-scrollbar::-webkit-scrollbar {
  width: 4px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  @apply bg-transparent;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  @apply bg-n-slate-4 rounded-full;
}
.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  @apply bg-n-slate-5;
}
</style>
